import React, { useState, useEffect } from 'react';
import GuidePage from './GuidePage';
import { 
  Settings, History, RefreshCcw, Wallet, Palette, 
  Languages, Trash2, Link as LinkIcon, LifeBuoy, Lightbulb, ChevronRight, Lock, 
  ArrowLeft, Camera, ShieldCheck, Ticket, Coins, User, Mail, CheckCircle2,
  Cpu, Activity, Loader2, Send, HelpCircle 
} from 'lucide-react';

const Profile = ({ points, setActiveTab }) => {
  const [currentView, setCurrentView] = useState('main'); 
  const [showLock, setShowLock] = useState(false);
  const [activeFeature, setActiveFeature] = useState('');
  const [profilePic, setProfilePic] = useState(null);
  
  const [ticketStep, setTicketStep] = useState('idle'); // idle, scanning, generating, generated
  const [ticketID, setTicketID] = useState('');
  
  const [userData, setUserData] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com'
  });

  
  useEffect(() => {
    if (ticketStep === 'scanning') {
      const timer = setTimeout(() => {
        setTicketStep('generating');
      }, 2500);
      return () => clearTimeout(timer);
    }
    
    if (ticketStep === 'generating') {
      const timer = setTimeout(() => {
        setTicketID(`KS-${Math.floor(100000 + Math.random() * 900000)}`);
        setTicketStep('generated');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [ticketStep]);

  const restrictedFeatures = ['Wallet', 'Account History', 'Connect'];

  const handleAction = (label) => {
    if (restrictedFeatures.includes(label)) {
      setActiveFeature(label);
      setShowLock(true);
    } else if (label === 'Support') {
      setCurrentView('support');
      setTicketStep('scanning');
    } else if (label === 'How to Play') {
      setCurrentView('guide');
    } else {
      const viewMap = {
        'Change Language': 'language',
        'Settings': 'settings'
      };
      setCurrentView(viewMap[label] || 'main');
    }
  };

  const handleGoHome = () => {
    if (setActiveTab) setActiveTab('home');
    setCurrentView('main');
  };

  
  if (currentView === 'guide') {
    return <GuidePage onClose={() => setCurrentView('main')} />;
  }

  
  const HeaderBack = ({ title }) => (
    <div className="flex items-center gap-4 mb-6">
      <button onClick={() => {setCurrentView('main'); setTicketStep('idle');}} className="bg-navy text-white p-2 rounded-xl shadow-lg active:scale-90 transition-all">
        <ArrowLeft size={20} />
      </button>
      <h2 className="text-xl font-black text-navy uppercase italic tracking-tighter">{title}</h2>
    </div>
  );

  
  if (currentView === 'main') {
    return (
      <div className="max-w-md mx-auto bg-bg-gray min-h-screen pb-24 relative animate-in fade-in duration-300">
        {}
        {showLock && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-navy/80 backdrop-blur-sm">
            <div className="bg-white w-full max-w-xs rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-amazon-yellow animate-in zoom-in duration-300">
              <div className="bg-gradient-to-b from-amazon-yellow to-gold p-6 text-center">
                <div className="bg-navy w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-2 border-4 border-white/30 shadow-lg">
                  <Lock className="text-amazon-yellow animate-pulse" size={24} />
                </div>
                <h3 className="text-navy font-black text-lg italic uppercase tracking-tighter leading-none">Feature Locked</h3>
              </div>
              <div className="p-6 text-center">
                <p className="text-navy font-black text-xs mb-1 uppercase">{activeFeature}</p>
                <p className="text-gray-500 text-[10px] font-bold mb-5 italic leading-tight">Please login to access secure point management.</p>
                <button onClick={() => setShowLock(false)} className="w-full bg-navy text-amazon-yellow py-3 rounded-2xl font-black text-xs shadow-lg uppercase active:scale-95 transition-all tracking-widest">UNDERSTOOD</button>
              </div>
            </div>
          </div>
        )}

        {}
        <header className="bg-navy sticky top-0 z-50 shadow-xl p-3 border-b border-white/10">
          <div className="flex justify-between items-center">
            <button onClick={handleGoHome} className="text-2xl font-black text-amazon-yellow italic tracking-tighter uppercase active:scale-95 transition-all">
              Kolkata <span className="text-white">Super</span>
            </button>
            <div className="bg-white/10 px-4 py-1.5 rounded-2xl border border-white/20 flex items-center">
              <Coins size={18} className="text-amazon-yellow mr-2" />
              <span className="text-white font-black text-sm">{points} KPS</span>
            </div>
          </div>
        </header>

        {}
        <div className="bg-navy p-10 text-center border-b-4 border-orange rounded-b-[4rem] shadow-2xl">
          <div className="relative w-28 h-28 mx-auto mb-4 group">
            <div className="w-full h-full bg-gradient-to-tr from-amazon-yellow to-gold rounded-full flex items-center justify-center text-navy text-4xl font-black border-4 border-white/20 shadow-xl overflow-hidden">
              {profilePic ? <img src={profilePic} alt="P" className="w-full h-full object-cover" /> : userData.name.charAt(0)}
            </div>
            <button onClick={() => setCurrentView('photo')} className="absolute bottom-0 right-0 bg-orange text-white p-2 rounded-full border-4 border-navy hover:scale-110 transition-transform shadow-lg">
              <Camera size={18} />
            </button>
          </div>
          <h2 className="text-white text-2xl font-black italic tracking-tighter uppercase leading-none">{userData.name}</h2>
          <p className="text-amazon-yellow/60 text-[10px] font-bold mt-1 uppercase tracking-widest">{userData.email}</p>
          <button onClick={() => setCurrentView('settings')} className="mt-4 bg-white/10 hover:bg-white/20 text-amazon-yellow text-[10px] font-black py-2 px-6 rounded-full border border-white/10 transition-all uppercase flex items-center gap-2 mx-auto">
             <Settings size={14}/> Edit Account Details
          </button>
        </div>

        {}
        <div className="mt-8 px-4 space-y-3">
          {[
            { icon: <Wallet size={20}/>, label: 'Wallet', sub: 'Balance & Transactions', color: 'text-orange' },
            { icon: <History size={20}/>, label: 'Account History', sub: 'Your bet records', color: 'text-blue-600' },
            { icon: <HelpCircle size={20}/>, label: 'How to Play', sub: 'User Guide & Rules', color: 'text-purple-600' },
            { icon: <Languages size={20}/>, label: 'Change Language', sub: 'Select app language', color: 'text-cyan-500' },
            { icon: <LifeBuoy size={20}/>, label: 'Support', sub: 'Get help 24/7', color: 'text-red-500' },
          ].map((item, index) => (
            <button key={index} onClick={() => handleAction(item.label)} className="w-full bg-white flex items-center p-4 rounded-2xl shadow-sm border border-amazon-border hover:border-amazon-yellow transition-all active:scale-[0.97] group">
              <div className={`p-3 rounded-2xl bg-gray-50 ${item.color} shadow-inner`}>{item.icon}</div>
              <div className="ml-4 text-left flex-grow">
                <p className="text-sm font-black text-navy uppercase tracking-tighter leading-none">{item.label}</p>
                <p className="text-[10px] text-gray-400 font-bold mt-1">{item.sub}</p>
              </div>
              <ChevronRight size={18} className="text-gray-300 group-hover:text-amazon-yellow" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  
  return (
    <div className="max-w-md mx-auto bg-bg-gray min-h-screen p-6 animate-in slide-in-from-right duration-300">
      <HeaderBack title={currentView === 'support' ? 'AI Support Ticket' : currentView} />

      <div className="bg-white p-6 rounded-[2.5rem] shadow-xl border-b-8 border-amazon-yellow">
        
        {}
        {currentView === 'support' && (
          <div className="text-center min-h-[300px] flex flex-col justify-center">
             {ticketStep === 'scanning' && (
                <div className="animate-in fade-in duration-500">
                   <div className="relative w-24 h-24 mx-auto mb-6">
                      <Cpu size={80} className="text-navy absolute inset-0 animate-spin-slow opacity-10" />
                      <Activity size={40} className="text-orange absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                   </div>
                   <h3 className="text-navy font-black italic uppercase tracking-tighter text-xl">System Scanning...</h3>
                   <div className="w-full bg-gray-100 h-1.5 rounded-full mt-6 overflow-hidden">
                      <div className="bg-amazon-yellow h-full w-1/2 animate-infinite-scroll"></div>
                   </div>
                </div>
             )}

             {ticketStep === 'generating' && (
                <div className="animate-in zoom-in duration-500">
                   <Loader2 size={60} className="text-navy mx-auto mb-6 animate-spin" />
                   <h3 className="text-navy font-black italic uppercase tracking-tighter text-xl">Creating Ticket</h3>
                </div>
             )}

             {ticketStep === 'generated' && (
                <div className="animate-in slide-in-from-bottom duration-500">
                   <div className="bg-navy text-white p-6 rounded-3xl mb-6 relative shadow-2xl border-b-4 border-orange">
                      <p className="text-amazon-yellow text-[9px] font-black tracking-widest uppercase mb-1">Ticket ID</p>
                      <h2 className="text-3xl font-black italic tracking-tighter">{ticketID}</h2>
                   </div>
                   <textarea placeholder="Describe your issue..." className="w-full p-4 bg-bg-gray rounded-2xl outline-none border-2 border-transparent focus:border-amazon-yellow font-bold text-navy h-32 mb-4 resize-none" />
                   <button className="w-full bg-navy text-amazon-yellow py-4 rounded-2xl font-black uppercase flex items-center justify-center gap-3 shadow-xl">
                      <Send size={18}/> Send Message
                   </button>
                </div>
             )}
          </div>
        )}

        {}
        {currentView === 'settings' && (
          <div className="space-y-4">
            <button onClick={() => setCurrentView('edit_name')} className="w-full flex items-center justify-between p-4 bg-bg-gray rounded-2xl border-2 border-transparent hover:border-amazon-yellow">
              <div className="flex items-center gap-3 font-bold text-navy text-sm"><User size={18} className="text-orange"/> Change Name</div>
              <ChevronRight size={16} className="text-gray-300"/>
            </button>
            <button onClick={() => setCurrentView('password')} className="w-full flex items-center justify-between p-4 bg-bg-gray rounded-2xl border-2 border-transparent hover:border-amazon-yellow">
              <div className="flex items-center gap-3 font-bold text-navy text-sm"><Lock size={18} className="text-red-500"/> Reset Password</div>
              <ChevronRight size={16} className="text-gray-300"/>
            </button>
          </div>
        )}

        {}
        {currentView === 'photo' && (
          <div className="text-center py-6">
            <div className="w-32 h-32 bg-bg-gray rounded-full mx-auto mb-8 flex items-center justify-center border-4 border-dashed border-gray-300 relative overflow-hidden">
               {profilePic ? <img src={profilePic} alt="P" className="w-full h-full object-cover" /> : <Camera className="text-gray-300" size={40} />}
            </div>
            <input type="file" id="file" className="hidden" onChange={(e) => { if(e.target.files[0]) setProfilePic(URL.createObjectURL(e.target.files[0])); }} />
            <label htmlFor="file" className="block w-full bg-navy text-amazon-yellow py-4 rounded-2xl font-black cursor-pointer text-center">UPLOAD PHOTO</label>
          </div>
        )}

        {currentView === 'language' && (
          <div className="grid gap-3">
            {['English', 'বাংলা', 'हिन्दी'].map((l) => (
              <button key={l} onClick={() => setCurrentView('main')} className="w-full p-4 rounded-2xl bg-bg-gray flex justify-between items-center font-black text-navy text-sm">
                {l} <CheckCircle2 size={16} className="text-amazon-yellow" />
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
        @keyframes scroll { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        .animate-infinite-scroll { animation: scroll 1.5s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default Profile;